// Inbox focus controller - manages keyboard navigation between inbox entries.
// Provides j/k navigation and visual focus ring on the currently focused item.
// Mount on the inbox entries container.
//
// Targets:
//   entry - each inbox entry card wrapper
//
// Dispatches:
//   inbox-focus:changed - when the focused entry changes (detail: { entry, index })

import { Controller } from "@hotwired/stimulus"

const FOCUS_CLASSES = [
  "ring-2", "ring-teal-500", "dark:ring-teal-400",
  "rounded-xl", "transition-shadow"
]

export default class extends Controller {
  static targets = ["entry"]

  connect() {
    this.currentIndex = -1
    this.keyboardActive = false
    this.registerShortcuts()
  }

  disconnect() {
    this.deregisterShortcuts()
    this.clearFocus()
  }

  entryTargetConnected() {
    // After Turbo replaces frame content, targets reconnect but the controller
    // persists (it lives outside the frame). Restore focus to the same index
    // so the user can keep processing inbox items without pressing j/k again.
    // Skip scrolling — the entry is already visible from the replacement.
    if (this.entryTargets.length > 0 && !this._hasFocusedEntry()) {
      const index = Math.min(Math.max(0, this.currentIndex), this.entryTargets.length - 1)
      this.focusIndex(index, { scroll: false })
    }
  }

  entryTargetDisconnected() {
    // No-op: focus restoration is handled by entryTargetConnected after
    // Turbo replaces the frame content.
  }

  next() {
    this.keyboardActive = true
    if (this.entryTargets.length === 0) return
    const nextIndex = this.currentIndex < this.entryTargets.length - 1
      ? this.currentIndex + 1
      : 0
    this.focusIndex(nextIndex)
  }

  previous() {
    this.keyboardActive = true
    if (this.entryTargets.length === 0) return
    const prevIndex = this.currentIndex > 0
      ? this.currentIndex - 1
      : this.entryTargets.length - 1
    this.focusIndex(prevIndex)
  }

  focusIndex(index, { scroll = true } = {}) {
    if (index < 0 || index >= this.entryTargets.length) return

    this.clearFocus()
    this.currentIndex = index
    const entry = this.entryTargets[index]
    if (!entry) return

    FOCUS_CLASSES.forEach(cls => entry.classList.add(cls))
    entry.dataset.focused = "true"
    if (this.keyboardActive) entry.dataset.navHints = "true"
    if (scroll) entry.scrollIntoView({ behavior: "auto", block: "nearest" })

    this.dispatch("changed", { detail: { entry, index } })
  }

  clearFocus() {
    this.entryTargets.forEach((entry) => {
      FOCUS_CLASSES.forEach(cls => entry.classList.remove(cls))
      delete entry.dataset.focused
      delete entry.dataset.navHints
    })
  }

  get focusedEntry() {
    if (this.currentIndex < 0 || this.currentIndex >= this.entryTargets.length) return null
    return this.entryTargets[this.currentIndex]
  }

  // Private

  _hasFocusedEntry() {
    return this.entryTargets.some(e => e.dataset.focused === "true")
  }

  registerShortcuts() {
    this.registeredKeys = ["j", "k"]

    this.registeredKeys.forEach((key) => {
      const description = key === "j" ? "Next inbox item" : "Previous inbox item"
      const handler = key === "j" ? () => this.next() : () => this.previous()

      document.body.dispatchEvent(
        new CustomEvent("shortcut:register", {
          detail: { key, description, category: "Inbox", handler },
          bubbles: true,
        })
      )
    })
  }

  deregisterShortcuts() {
    (this.registeredKeys || []).forEach((key) => {
      document.body.dispatchEvent(
        new CustomEvent("shortcut:deregister", { detail: { key }, bubbles: true })
      )
    })
  }
}
